import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { CornerDownLeft, Search } from "lucide-react";
import { api, storePath } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { useStore } from "../lib/store";
import { useDebounced } from "../routes/products/ProductList";
import { cx, Spinner } from "./ui";
import { flatLinks, quickActions, settingsGroups } from "../lib/navigation";
import { NAV_STROKE, navIcons, type NavIcon } from "./nav-icons";
import type { Key } from "../locales/en";

type Hit = { type: "product" | "order" | "customer"; id: string; title: string; subtitle: string };
const order: Hit["type"][] = ["product", "order", "customer"];
const icons: Record<Hit["type"], NavIcon> = { product: "catalog", order: "orders", customer: "customers" };

// One row in the palette: a page, a settings section, a quick action, or a
// record found by search.
type Entry = { key: string; group: Key; icon: NavIcon; title: string; subtitle?: string; go: () => void };

// Cmd+K / Ctrl+K: jump to any page or settings section, run a quick action
// ("Create product"), or find products, orders and customers (specs 25 §6,
// redesign.md §4.5). It only navigates; it adds no actions of its own.
export function CommandPalette() {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
  const mac = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform);
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} aria-keyshortcuts={mac ? "Meta+K" : "Control+K"}
        className="flex h-9 items-center gap-2 rounded-md border border-line-strong bg-white px-2.5 text-sm text-ink-muted hover:bg-subtle hover:text-ink">
        <Search className="size-4 shrink-0" strokeWidth={NAV_STROKE} aria-hidden />
        <span className="sr-only sm:not-sr-only">{t("palette.open")}</span>
        <kbd aria-hidden className="hidden rounded-md border border-line bg-subtle px-1.5 font-mono text-[11px] text-ink-muted sm:inline">{mac ? "⌘K" : "Ctrl K"}</kbd>
      </button>
      {open && <Palette onClose={() => setOpen(false)} />}
    </>
  );
}

function Palette({ onClose }: { onClose: () => void }) {
  const { t } = useI18n();
  const { id, handle, can } = useStore();
  const navigate = useNavigate();
  const input = useRef<HTMLInputElement>(null);
  const [q, setQ] = useState("");
  const [active, setActive] = useState(0);
  const dq = useDebounced(q.trim(), 150);
  useEffect(() => { input.current?.focus(); }, []);
  const res = useQuery({
    queryKey: ["store", id, "palette", dq], enabled: !!id && dq !== "", staleTime: 10_000, retry: false,
    queryFn: () => api<{ results: Partial<Record<Hit["type"], Hit[]>> }>(storePath(id!, `/search?q=${encodeURIComponent(dq)}`)),
  });
  const to = (path: string) => () => { onClose(); navigate({ to: `/store/$handle${path}` as never, params: { handle } as never }); };
  const entries = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const match = (...xs: string[]) => needle === "" || xs.some((x) => x.toLowerCase().includes(needle));
    const out: Entry[] = [];
    for (const a of quickActions) {
      if (can(a.perm) && match(t(a.label))) out.push({ key: "a" + a.to, group: "palette.group.actions", icon: a.icon, title: t(a.label), go: to(a.to) });
    }
    const seen = new Set<string>();
    for (const { link, module, parent } of flatLinks(can)) {
      if (seen.has(link.to) || link.soon) continue;
      seen.add(link.to);
      const sub = [module.label ? t(module.label) : "", parent && parent.to !== link.to ? t(parent.label) : ""].filter(Boolean).join(" › ");
      if (match(t(link.label), sub)) out.push({ key: "p" + link.to, group: "palette.group.pages", icon: link.icon, title: t(link.label), subtitle: sub, go: to(link.to || "/") });
    }
    if (needle !== "") {
      for (const g of settingsGroups) {
        for (const sec of g.sections) {
          if (sec.perm && !can(sec.perm)) continue;
          if (match(t(sec.label), t(sec.hint), t(g.label))) {
            out.push({ key: "s" + sec.to, group: "palette.group.settings", icon: sec.icon, title: t(sec.label), subtitle: `${t("nav.settings")} › ${t(g.label)}`, go: to("/settings" + sec.to) });
          }
        }
      }
      for (const k of order) {
        for (const h of res.data?.results[k] ?? []) {
          const path = k === "product" ? `/products/${h.id}` : k === "order" ? `/orders/${h.id}` : `/customers/${h.id}`;
          out.push({ key: k + h.id, group: `palette.group.${k}` as Key, icon: icons[k], title: h.title, subtitle: h.subtitle, go: to(path) });
        }
      }
    }
    return needle === "" ? out.slice(0, 14) : out;
  }, [q, res.data, can, t]);
  useEffect(() => setActive(0), [q]);
  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") onClose();
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => Math.min(a + 1, entries.length - 1)); }
    if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
    if (e.key === "Enter" && entries[active]) { e.preventDefault(); entries[active].go(); }
  };
  const groups: Key[] = [];
  for (const e of entries) if (!groups.includes(e.group)) groups.push(e.group);
  let i = -1;
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-ink/20 p-4 pt-[12vh] backdrop-blur-[2px]" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div role="dialog" aria-modal aria-label={t("palette.open")} className="anim-pop w-full max-w-xl overflow-hidden rounded-2xl border border-line bg-white shadow-pop" onKeyDown={onKey}>
        <div className="flex items-center gap-2 border-b border-line px-4">
          <Search className="size-[18px] shrink-0 text-ink-subtle" strokeWidth={NAV_STROKE} aria-hidden />
          <input ref={input} value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("palette.placeholder")} dir="auto"
            role="combobox" aria-expanded={entries.length > 0} aria-controls="palette-results" aria-activedescendant={entries[active] ? `palette-${active}` : undefined}
            className="h-14 w-full bg-transparent text-[15px] outline-none placeholder:text-ink-subtle focus-visible:ring-0" />
          {res.isFetching && <Spinner className="size-4 text-ink-subtle" />}
          <kbd className="rounded-md border border-line px-1.5 font-mono text-[11px] text-ink-muted">Esc</kbd>
        </div>
        <div id="palette-results" role="listbox" className="max-h-[60vh] overflow-y-auto p-2">
          {entries.length === 0 && dq !== "" && !res.isFetching && <p className="px-3 py-8 text-center text-sm text-ink-muted">{t("palette.empty", { q: dq })}</p>}
          {groups.map((g) => (
            <div key={g} className="pb-1">
              <p className="px-3 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-[0.06em] text-ink-subtle">{t(g)}</p>
              {entries.filter((e) => e.group === g).map((e) => {
                i += 1;
                const idx = i;
                const Icon = navIcons[e.icon];
                return (
                  <button key={e.key} id={`palette-${idx}`} type="button" role="option" aria-selected={idx === active}
                    onMouseEnter={() => setActive(idx)} onClick={e.go}
                    className={cx("flex w-full items-center gap-3 rounded-lg px-3 py-2 text-start text-sm", idx === active ? "bg-brand-tint" : "hover:bg-subtle")}>
                    <Icon className={cx("size-4 shrink-0", idx === active ? "text-brand" : "text-ink-muted")} strokeWidth={NAV_STROKE} aria-hidden />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate font-medium text-ink" dir="auto">{e.title}</span>
                      {e.subtitle && <span className="block truncate text-xs text-ink-muted" dir="auto">{e.subtitle}</span>}
                    </span>
                    {idx === active && <CornerDownLeft className="size-3.5 shrink-0 text-ink-subtle" aria-hidden />}
                  </button>
                );
              })}
            </div>
          ))}
          {res.isError && dq !== "" && <p className="px-3 py-2 text-xs text-ink-subtle">{t("palette.unavailable")}</p>}
        </div>
        <div className="border-t border-line bg-subtle px-4 py-2 text-xs text-ink-muted">{t("palette.hint")}</div>
      </div>
    </div>
  );
}
